import { useState } from 'react';
import { useCourses } from '../../hooks/useCourses';
import { useActivity, useCourseStatistics, useGlobalStatistics } from '../../hooks/useStatistics';
import { Select } from '../../components/ui/Select';
import { StreakPanel } from '../../components/brand/StreakPanel';
import { Karti } from '../../components/brand/Karti';
import { KartiCoachBubble } from '../../components/brand/KartiCoach';
import { activityContext } from '../../lib/karti';
import { CourseGlyph } from '../../components/brand/GroupBar';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PHASE_COLORS } from '../../lib/utils';

function Panel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-panel border-2.5 border-ink bg-cream p-4 ${className}`}>{children}</div>;
}

export default function StatisticsPage() {
  const { data: courses } = useCourses();
  const { data: globalStats } = useGlobalStatistics();
  const { data: activity } = useActivity();
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);

  const { data: stats, isLoading, isError } = useCourseStatistics(selectedCourseId || 0);

  const myCourses = courses?.filter((c) => c.role) || [];

  const handleCourseSelect = (courseId: string) => {
    setSelectedCourseId(courseId ? parseInt(courseId) : null);
  };

  const Title = (
    <h1 className="text-[34px] font-extrabold leading-none tracking-[-0.03em]">Statistik</h1>
  );

  if (myCourses.length === 0) {
    return (
      <div className="space-y-5">
        {Title}
        <Panel className="p-8 text-center shadow-hard">
          <Karti className="mx-auto mb-3" size={80} mood="sleep" />
          <h3 className="text-[22px] font-bold">Keine Kurse</h3>
          <p className="mt-1 text-body">Du musst zuerst einem Kurs beitreten, um Statistiken zu sehen.</p>
        </Panel>
      </div>
    );
  }

  const total = stats?.phaseDistribution.reduce((a, p) => a + p.count, 0) ?? 0;
  const counts = Array.from({ length: 7 }, (_, i) => stats?.phaseDistribution.find((p) => p.phase === i + 1)?.count ?? 0);
  const groups = [
    { label: 'NEU 1–2', v: counts[0] + counts[1], c: PHASE_COLORS[0] },
    { label: 'IN ARBEIT 3–5', v: counts[2] + counts[3] + counts[4], c: PHASE_COLORS[3] },
    { label: 'GELERNT 6–7', v: counts[5] + counts[6], c: PHASE_COLORS[6] },
  ];

  return (
    <div className="space-y-5 animate-fadein">
      {Title}

      {activity && <StreakPanel activity={activity} />}
      <KartiCoachBubble screen="statistics" ctx={activityContext(activity)} size={56} compact />

      <div className="w-full sm:w-72">
        <Select value={selectedCourseId?.toString() || ''} onChange={(e) => handleCourseSelect(e.target.value)}>
          <option value="">Kurs wählen...</option>
          {myCourses.map((course) => (
            <option key={course.id} value={course.id.toString()}>
              {course.name}
            </option>
          ))}
        </Select>
      </div>

      {!selectedCourseId ? (
        <Panel className="shadow-hard">
          <h3 className="text-[22px] font-bold">Kurs auswählen</h3>
          <p className="mt-1 text-body">Wähle einen Kurs aus dem Menü, um Statistiken anzuzeigen.</p>
        </Panel>
      ) : isLoading ? (
        <div className="space-y-4" aria-busy="true">
          <div className="skeleton h-28 w-full !rounded-hero" />
          <div className="skeleton h-40 w-full !rounded-panel" />
        </div>
      ) : isError || !stats ? (
        <Panel className="flex items-center gap-3 shadow-hard">
          <span className="h-3 w-3 shrink-0 rounded-full border-2 border-ink bg-wrong" />
          <div>
            <h3 className="font-bold">Keine Daten</h3>
            <p className="text-sm text-body">Für diesen Kurs sind noch keine Statistiken verfügbar.</p>
          </div>
        </Panel>
      ) : (
        <>
          {/* Ink summary panel */}
          <div className="space-y-4 rounded-hero bg-ink p-4 text-paper">
            <div className="flex items-end justify-between gap-3">
              <div className="flex items-baseline gap-3">
                <span className="text-[56px] font-extrabold leading-none tracking-tight text-marker">
                  {stats.overall.successRate.toFixed(0)}%
                </span>
                <span className="text-base font-bold">Erfolgsrate</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 border-t-[1.5px] border-muted-foreground pt-3">
              {[
                { l: 'ANTWORTEN', v: stats.overall.totalAnswers },
                { l: 'RICHTIG', v: stats.overall.correctAnswers },
                { l: 'Ø ZEIT', v: `${(stats.overall.averageTime / 1000).toFixed(1)}s` },
              ].map((x) => (
                <div key={x.l}>
                  <div className="text-[26px] font-extrabold leading-none">{x.v}</div>
                  <div className="mt-1 font-mono text-[10px] font-bold tracking-[0.07em] text-on-ink-sub">{x.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Phase distribution */}
          <Panel className="space-y-3">
            <div className="label-mono">{total} VOKABELN NACH PHASE</div>
            {total === 0 ? (
              <p className="py-6 text-center text-body">Noch keine Daten verfügbar</p>
            ) : (
              <>
                <div className="flex h-[30px] overflow-hidden rounded-key border-2.5 border-ink bg-track">
                  {counts.map((c, i) =>
                    c > 0 ? (
                      <div
                        key={i}
                        className="origin-left animate-grow"
                        style={{
                          width: `${(c / total) * 100}%`,
                          background: PHASE_COLORS[i],
                          borderLeft: i > 0 ? '1.5px solid #161512' : undefined,
                          animationDelay: `${i * 50}ms`,
                        }}
                        title={`Phase ${i + 1}: ${c}`}
                      />
                    ) : null
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {groups.map((g) => (
                    <div key={g.label}>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-[3px] border-[1.5px] border-ink"
                          style={{ background: g.c }}
                        />
                        <span className="font-mono text-[10px] font-bold tracking-[0.05em] text-muted-foreground">
                          {g.label}
                        </span>
                      </div>
                      <div className="text-[26px] font-extrabold leading-tight">{g.v}</div>
                    </div>
                  ))}
                </div>

                {/* per-phase rows */}
                <div className="space-y-2 border-t-[1.5px] border-divider pt-3">
                  {stats.phaseDistribution.map((p) => (
                    <div key={p.phase} className="flex items-center gap-3">
                      <span className="w-8 font-mono text-xs font-bold">P{p.phase}</span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-track">
                        <div
                          className="h-full origin-left animate-grow rounded-full border-r-2 border-ink"
                          style={{ width: `${p.percentage}%`, background: PHASE_COLORS[p.phase - 1] }}
                        />
                      </div>
                      <span className="w-20 text-right font-mono text-xs font-bold">
                        {p.count} · {p.percentage.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Panel>

          {/* Timeline */}
          <Panel className="space-y-3">
            <div className="label-mono">ERFOLGSRATE · LETZTE 30 TAGE</div>
            {stats.timeline.length === 0 ? (
              <p className="py-6 text-center text-body">Noch keine Daten verfügbar</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={stats.timeline} margin={{ left: -16, right: 8, top: 8 }}>
                  <CartesianGrid stroke="#D9D2C2" strokeDasharray="0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#161512"
                    tick={{ fontFamily: 'JetBrains Mono', fontSize: 11, fill: '#5E5A52' }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getDate()}.${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    stroke="#161512"
                    tick={{ fontFamily: 'JetBrains Mono', fontSize: 11, fill: '#5E5A52' }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#FFFCF5',
                      border: '2.5px solid #161512',
                      borderRadius: 12,
                      boxShadow: '3px 3px 0 #161512',
                      fontWeight: 700,
                    }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString('de-DE')}
                    formatter={(value: number) => [`${value.toFixed(1)}%`, 'Erfolgsrate']}
                  />
                  <Line
                    type="linear"
                    dataKey="successRate"
                    stroke="#161512"
                    strokeWidth={2.5}
                    name="Erfolgsrate (%)"
                    dot={{ r: 4, fill: '#F4D03F', stroke: '#161512', strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: '#F4D03F', stroke: '#161512', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </>
      )}

      {/* Per course */}
      {globalStats && globalStats.length > 0 && (
        <>
          <h2 className="label-mono !text-xs">PRO KURS</h2>
          <div className="space-y-3">
            {globalStats.map((g, i) => {
              const pct = Math.round(g.overallProgress);
              return (
                <div key={g.courseId} className="flex items-center gap-3">
                  <CourseGlyph name={g.courseName} size={34} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[15px] font-bold">{g.courseName}</span>
                      <span className="font-mono text-xs font-bold">{pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-[3px] bg-track">
                      <div
                        className="h-full origin-left animate-grow bg-ink"
                        style={{ width: `${pct}%`, animationDelay: `${i * 120}ms` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
